import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { overlayPortalContainerId } from '../../Utils';
import { primaryColor1 } from '../Utils';
import { Icon } from './index';

//#region Styling
const Container = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const Root = styled.span`
  cursor: help;
`;
const Tooltip = styled.div`
  position: fixed;
  z-index: 3000;
  width: 18rem;
  font-size: 0.95rem;
  line-height: 1.4;
  text-transform: none;
  padding: 0.8rem;
  opacity: 0;
  transition: opacity 0.15s ease;
  background-color: ${primaryColor1};
  color: #fff;
`;
//#endregion

interface Props {
  index: number;
  term: string;
  explanation: string;
  isHelpActive: boolean;
  styles: React.CSSProperties;
}

const HelpTooltip = (props: Props) => {
  const {index, term, explanation, isHelpActive, styles} = props;
  const rootEl = useRef<HTMLDivElement | null>(null);
  const tooltipEl = useRef<HTMLDivElement | null>(null);
  const overlayPortalContainerNodeRef = useRef<HTMLElement | null>(null);

  const [isTooltipShown, setIsTooltipShown] = useState<boolean>(false);
  const onMouseEnter = () => setIsTooltipShown(true);
  const onMouseLeave = () => setIsTooltipShown(false);

  useLayoutEffect(() => {
    const node = document.querySelector<HTMLElement>(`#${overlayPortalContainerId}`);
    overlayPortalContainerNodeRef.current = node;
    if (tooltipEl.current !== null && rootEl.current !== null) {
      const tooltipElm = tooltipEl.current;
      const {top, left} = rootEl.current.getBoundingClientRect();
      const tooltipSpacing = 5;
      const tooltipHeight = tooltipElm.offsetHeight;
      const tooltipWidth = tooltipElm.offsetWidth;
      const tooltipTopValue = top + rootEl.current.offsetHeight + tooltipSpacing;
      if ((tooltipTopValue + tooltipHeight) > window.innerHeight) {
        // tooltip will exceed the windows height
        tooltipElm.style.top = `${top - tooltipSpacing - tooltipHeight}px`;
      } else {
        tooltipElm.style.top = `${tooltipTopValue}px`;
      }
      if ((left + tooltipWidth) > window.innerWidth) {
        // tooltip will exceed the windows width
        tooltipElm.style.left = `${window.innerWidth - tooltipWidth - tooltipSpacing}px`;
      } else {
        tooltipElm.style.left = `${left}px`;
      }
      tooltipElm.style.opacity = '1';
    }
  }, [isTooltipShown]);
  const overlayPortalContainerNode = overlayPortalContainerNodeRef.current;

  let tooltip: React.ReactPortal | null;
  let icon: React.ReactElement | null;
  if (isHelpActive === true) {
    icon = <Icon>{index}</Icon>;
    if (isTooltipShown !== false && overlayPortalContainerNode !== null) {
      tooltip = ReactDOM.createPortal((
        <Tooltip ref={tooltipEl}>
          {term} - {explanation}
        </Tooltip>
      ), overlayPortalContainerNode);
    } else {
      tooltip = null;
    }
  } else {
    icon = null;
    tooltip = null;
  }

  return (
    <Container>
      <Root
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={rootEl}
        style={styles}
      >
        {icon}
        {tooltip}
      </Root>
    </Container>
  );
};

export default HelpTooltip;
